/** @format */
import React, { useEffect, useState } from "react";
import { FaRegBell } from "react-icons/fa";
import { HiUserCircle } from "react-icons/hi2";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import Cookies from "js-cookie";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logoAnimation from "../../../assets/12356.png";

const SOCKET_URL = process.env.REACT_APP_FRONTEND_URL;
const API_URL = process.env.REACT_APP_FRONTEND_URL;

const socket = io(SOCKET_URL, {
  transports: ["websocket"],
});

const Navbar = () => {
  const [employeeFullname, setEmployeeFullname] = useState("User");
  const [username, setUsername] = useState("");
  const [employeeNo, setEmployeeNo] = useState("");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const navigate = useNavigate();

  // Load user info from cookies
  useEffect(() => {
    const full = Cookies.get("employee_fullname");
    const user = Cookies.get("username");
    const empNo = Cookies.get("employee_no") || Cookies.get("username");
    if (full) setEmployeeFullname(full);
    if (user) setUsername(user);
    if (empNo) setEmployeeNo(empNo);
  }, []);

  useEffect(() => {
    if (!employeeNo) return;
    // Join with employeeNo (room key is always employee_no)
    socket.emit("join", employeeNo);
    // Unified notification handler for LEAVE + ATTENDANCE
    socket.on("notification", (data) => {
      console.log(":envelope_with_arrow: Incoming socket notification:", data);
      // Show toast depending on notification type
      if (data.type === "ATTENDANCE") {
        toast.warning(data.message, {
          position: "top-right",
          autoClose: 2500,
          theme: "colored",
        });
      } else if (data.type === "LEAVE") {
        toast.info(data.message, {
          position: "top-right",
          autoClose: 2500,
          theme: "colored",
        });
      } else {
        toast(data.message, {
          position: "top-right",
          autoClose: 2500,
          theme: "colored",
        });
      }
      setUnreadCount((prev) => prev + 1);
      // Native browser notification
      if ("Notification" in window && Notification.permission === "granted") {
        const notif = new Notification("Internal HRIS", {
          body: data.message,
          icon: logoAnimation,
        });
        notif.onclick = () => {
          window.focus();
          // Handle LEAVE notification navigation
          if (data.type?.toUpperCase() === "LEAVE" && data.leaveId) {
            navigate(`/leave-approve?leaveId=${data.leaveId}`);
          }
          // Handle ATTENDANCE missed punch → go to attendance page
          if (data.type?.toUpperCase() === "ATTENDANCE") {
            navigate("/attendance");
          }
        };
      }
    });
    // Cleanup when component unmounts
    return () => {
      socket.off("notification");
    };
  }, [employeeNo, navigate]);

  // Ask browser permission once
  useEffect(() => {
    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission();
    }
  }, []);

  const recalcUnread = (list) =>
    setUnreadCount(list.filter((n) => (n.is_read || 0) < 1).length);

  // Fetch notifications from API
  const fetchNotifications = async (pageNo = 1, limit = 8) => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API_URL}/v1/hris/notifications/get/${employeeNo}?page=${pageNo}&limit=${limit}`
      );
      const json = await res.json();

      if (json.success) {
        const merged =
          pageNo === 1 ? json.data : [...notifications, ...json.data];
        setNotifications(merged);
        setHasMore(pageNo < json.totalPages);
        setTotalPages(json.totalPages);
        recalcUnread(merged);
      } else {
        console.error("❌ API error:", json.message);
      }
    } catch (err) {
      console.error("❌ Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Open sidebar and load first page
  const openSidebar = () => {
    setSidebarOpen(true);
    setPage(1);
    if (employeeNo) fetchNotifications(1);
  };

  const loadMore = () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNotifications(nextPage);
  };

  // Navigate by notification type
  const getNotificationRoute = (n) => {
    const t = (n.type || "").toUpperCase();
    if (t === "LEAVE") {
      return n.related_entity_id
        ? `/leave-approve?leaveId=${n.related_entity_id}`
        : "/leave-approve";
    }
    return null;
  };

  // Tell backend it’s read (non-blocking)
  const markReadFireAndForget = (id) => {
    fetch(`${API_URL}/v1/hris/notifications/notifications/mark-read`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notificationIds: [id] }),
    }).catch((e) => console.error("PUT mark-read failed:", e));
  };

  const handleNotificationClick = (n) => {
    // 1. Optimistically mark as read in UI
    setNotifications((prev) => {
      const updated = prev.map((notif) =>
        notif.id === n.id ? { ...notif, is_read: 1 } : notif
      );
      recalcUnread(updated);
      return updated;
    });

    // 2. Fire and forget backend update
    markReadFireAndForget(n.id);

    // 3. Close sidebar & navigate
    setSidebarOpen(false);
    const dest = getNotificationRoute(n);
    if (dest) navigate(dest);
  };


  const confirmLogout = () => {
    localStorage.clear();
    Object.keys(Cookies.get()).forEach((cookie) => Cookies.remove(cookie));
    navigate("/login");
  };

  return (
    <>
      {/* Top Navbar */}
      <div className="bg-white rounded-lg shadow font-montserrat">
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-6">
            {/* 🔔 Bell */}
            <div
              className="relative mb-[10px] cursor-pointer"
              onClick={openSidebar}
            >
              <FaRegBell className="mr-2 w-6 h-6 text-gray-500" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -left-1 inline-flex items-center justify-center
                  min-w-[16px] h-4 px-[6px] rounded-full bg-red-500 text-white
                  text-[10px] leading-none ring-2 ring-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>

            {/* Name + username */}
            <div className="mr-[-5%] text-right">
              <p className="text-sm">{employeeFullname}</p>
              <p className="mt-[-2px] text-sm text-gray-700">{username}</p>
            </div>

            {/* User icon */}
            <div className="relative mb-[10px]">
              <button
                onClick={() => setShowLogoutConfirm(true)}
                className="flex items-center gap-1 text-gray-700 hover:text-blue-600"
              >
                <HiUserCircle className="w-8 h-8" />
                <span className="text-xs">▼</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar Notifications */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            {/* Background Blur */}
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
            />

            {/* Sidebar */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="fixed top-0 right-0 h-full w-full sm:w-[400px] bg-white shadow-2xl z-50 flex flex-col"
            >
              <div className="flex justify-between items-center border-b pb-4 px-6 pt-6">
                <h2 className="text-lg font-semibold text-gray-800">
                  Notifications
                </h2>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="text-gray-500 hover:text-red-500 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              {/* Notification list */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                {loading && page === 1 ? (
                  <p className="text-gray-500 text-center py-6">
                    Loading notifications...
                  </p>
                ) : notifications.length === 0 ? (
                  <p className="text-gray-500 text-center py-6">
                    No notifications
                  </p>
                ) : (
                  <AnimatePresence>
                    {notifications.map((n) => (
                      <motion.div
                        key={n.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.25 }}
                        onClick={() => handleNotificationClick(n)}
                        className={`border-b pb-2 last:border-0 cursor-pointer p-2 rounded transition
                          ${(n.is_read || 0) < 1
                            ? "bg-blue-50 font-semibold hover:bg-blue-100"
                            : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                          }`}
                      >
                        <p className="text-sm">{n.message}</p>
                        <small className="text-xs text-gray-500">
                          {n.timestamp
                            ? new Date(n.timestamp).toLocaleString()
                            : ""}
                        </small>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </div>

              {/* Footer See More */}
              {hasMore && (
                <div className="border-t p-4">
                  <button
                    onClick={loadMore}
                    className="w-full py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    See More
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Logout Confirmation */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-80 text-center shadow-lg">
            <h2 className="text-lg font-semibold mb-4">Confirm Logout</h2>
            <p className="mb-6">Are you sure you want to logout?</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast container */}
      <ToastContainer
        position="top-right"
        autoClose={2500}
        closeOnClick
        pauseOnHover
        theme="colored"
      />
    </>
  );
};

export default Navbar;
